import moment from "moment";
import { QuotaLevels } from "../enums/reservation.enum";
import { IOfferedSubjectByType, IOfferedSubjectData } from "../types";

export const normalizeText = (str: string | null | undefined) => {
  if (typeof str !== "string") return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export const getQuotaColor = (quotaAvailability: number) => {
  switch (true) {
    case quotaAvailability === QuotaLevels.HIGH:
      return "green";
    case quotaAvailability === QuotaLevels.MID:
      return "orange";
    case quotaAvailability === QuotaLevels.LOW:
      return "red";
    case quotaAvailability === QuotaLevels.NONE:
      return "gray";
    default:
      return "gray";
  }
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const parseHours = (time: number) => {
  const hour = String(time).split("");
  const minutes = hour.splice(-2).join("");
  return `${hour.join("")}:${minutes}`;
};

export function createSubjectSlug(text: string) {
  if (!text) {
    return "";
  }

  let slug = "";
  if (text && text.includes("-")) {
    const nombre = text
      .split("-")[0]
      .split(" ")
      .filter((t) => t !== "");

    const seccion = text
      .split("-")[1]
      .split(" ")
      .filter((t) => t !== "")[0];

    slug = nombre.map((word) => word.charAt(0)).join("") + " " + seccion;
  } else {
    const nombre = text.split(" ").filter((t) => t !== "");
    slug = nombre.map((word) => word.charAt(0)).join("");
  }

  return slug;
}

export function copyTextToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export const isNombreADesignar = (nombre?: string | string[] | null) => {
  if (nombre == null) return true;
  const value = Array.isArray(nombre) ? nombre.filter(Boolean).join(", ") : String(nombre);
  const normalized = value.toLowerCase().trim();
  if (!normalized) return true;
  if (normalized.includes("null")) return true;
  if (normalized.includes("a designar")) return true;
  return /^[\s.,;:\-_/\\]+$/.test(normalized);
};

export const findProfeTitular = (subjectData: IOfferedSubjectData | undefined): string | null => {
  if (!subjectData) return null;
  let profe;
  for (const cursada of subjectData?.subjectsByType) {
    for (const horario of cursada?.subjectInfo) {
      const tipoClase = horario.values.tipoClase;
      if (horario.values.rol === "Profesor" && (tipoClase === "Teórica" || tipoClase === "Teórica-Práctica")) {
        profe = horario.values.profesores[0];
        if (profe) break;
      }
    }
    if (profe) break;
  }
  if (profe == null || isNombreADesignar(profe)) return null;
  return profe;
};

export function extraerNombreMateria(texto: string | undefined | null) {
  if (!texto || typeof texto !== 'string') return ""; // Maneja undefined, null o valores no string
  let indiceUltimoGuion = texto.lastIndexOf("-");
  if (indiceUltimoGuion === -1) return texto; // Si no hay "-", devuelve el texto original
  return texto.slice(0, indiceUltimoGuion).trim(); // Recorta y elimina espacios finales
};

export function extraerSeccion(texto: string | undefined | null) {
  if (!texto || typeof texto !== 'string') return null; // Maneja undefined, null o valores no string
  const regex = /- (S\d+)/;
  const match = texto.match(regex);
  return match ? match[1] : null;
}

/** Texto que sigue a la sección, por ejemplo `(2405)` en `Historia del arte - S1 (2405)`. */
export function extraerCodigoMateria(texto: string | undefined | null) {
  if (!texto || typeof texto !== "string") return "";
  const match = texto.match(/- S\d+\s*(.*)$/i);
  return match?.[1]?.trim() ?? "";
}


export const formatDateToDDMMYYYY = (isoDate: string): string => {
  return moment.utc(isoDate).format("DD/MM/YYYY");
};

export const formatDateToDDMM = (isoDate: string): string => {
  return moment.utc(isoDate).format("DD/MM");
};

export function dateHasPassed(dateString: string): boolean {
  const targetDate = new Date(dateString);
  const currentDate = new Date();
  return targetDate < currentDate;
}

export const parseArgentinaDate = (date: string) => {
  return new Date(Date.parse(date)); 
};

export const getTimeInMiliseconds = (date: string) => {
  if (!date) return Date.now();
  return Date.parse(date); 
};